/**
 * AlertPolygonLayer.tsx — مضلّعات تحذيرات NWS فوق الرادار.
 *
 * ── لماذا الحدّ عريض والتعبئة شبه معدومة ─────────────────────────────────────
 * لأن ما تحت المضلّع هو المعلومة: خلية الرادار الحمراء داخل تحذير الإعصار القمعي هي
 * ما ينظر إليه المستخدم. تعبئةٌ ملموسة تُغبّش الانعكاسية وتقلب الطبقة من إطارٍ إلى
 * حجاب — وهو ما تتجنّبه RadarScope وWindy معاً: خطٌّ صريح وداخلٌ يكاد يكون شفّافاً.
 *
 * واللون يأتي من خاصّية `color` المحسوبة في الخادم لا من تعبير `match` هنا: جدول
 * الأحداث واحد في مكان واحد، فإضافة نوع تحذير جديد لا تتطلّب تعديل الواجهة.
 */
import { useEffect, useRef, useState } from 'react';
import { Source, Layer } from 'react-map-gl/maplibre';
import { getActiveAlerts, EMPTY_ALERTS, type AlertCollection } from '../../services/nwsAlertsService';

interface Props {
    enabled: boolean;
}

/** التحذيرات تُصدَر وتنتهي بالدقيقة، والخادم يخزّن 60 ثانية — فلا فائدة من أسرع. */
const POLL_MS = 60_000;

export function AlertPolygonLayer({ enabled }: Props) {
    const [data, setData] = useState<AlertCollection>(EMPTY_ALERTS);
    // آخر مجموعة ناجحة تبقى معروضة عند تعذّر التحديث — راجع شرح `getActiveAlerts`.
    const lastGood = useRef<AlertCollection>(EMPTY_ALERTS);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;

        const pull = () => {
            void getActiveAlerts().then((c) => {
                if (cancelled || c === null) return;
                lastGood.current = c;
                setData(c);
            });
        };

        pull();
        const timer = window.setInterval(pull, POLL_MS);
        return () => { cancelled = true; window.clearInterval(timer); };
    }, [enabled]);

    if (!enabled) return null;

    return (
        <Source id="nws-alerts" type="geojson" data={data}>
            <Layer
                id="nws-alerts-fill"
                type="fill"
                paint={{
                    'fill-color': ['get', 'color'],
                    'fill-opacity': 0.08,
                }}
            />
            <Layer
                id="nws-alerts-line"
                type="line"
                paint={{
                    'line-color': ['get', 'color'],
                    // يغلظ مع التقريب: عند رؤية القارّة كلّها يكون المضلّع بضع بكسلات،
                    // فخطٌّ رفيع يختفي؛ وعند التكبير يصير الغليظ حجاباً على ما تحته.
                    'line-width': ['interpolate', ['linear'], ['zoom'], 3, 1.2, 7, 2.2, 11, 3],
                    'line-opacity': 0.95,
                }}
            />
        </Source>
    );
}
